<template>
    <div class="pb-10">
        <!-- Cabeçalho -->
        <header class="flex flex-wrap items-end justify-between gap-4 py-4">
            <div>
                <p
                    class="text-[10px] font-medium uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500"
                >
                    Financeiro · Exposição
                </p>
                <h2
                    class="mt-1 text-xl font-semibold tracking-tight text-gray-900 dark:text-white"
                >
                    Contas com Saldo Negativo
                </h2>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Saldo vivo lido direto da
                    <span class="font-mono text-gray-600 dark:text-gray-300"
                        >account</span
                    >
                    do produtor. O escopo define qual saldo entra na conta.
                </p>
            </div>

            <div class="flex items-center gap-2">
                <span
                    v-if="lastUpdatedAt"
                    class="hidden font-mono text-[10px] uppercase tracking-widest text-gray-400 sm:inline dark:text-gray-500"
                >
                    Atualizado {{ lastUpdatedAt }}
                </span>
                <button
                    type="button"
                    :disabled="loading"
                    class="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    @click="fetch()"
                >
                    <ArrowPathIcon
                        class="h-3.5 w-3.5"
                        :class="{ 'animate-spin': loading }"
                        aria-hidden="true"
                    />
                    Atualizar
                </button>
                <button
                    type="button"
                    :disabled="exporting || loading || summary.accounts === 0"
                    class="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                    @click="exportCsv"
                >
                    <ArrowDownTrayIcon class="h-3.5 w-3.5" aria-hidden="true" />
                    {{ exporting ? "Exportando..." : "Exportar CSV" }}
                </button>
            </div>
        </header>

        <!-- Indicadores -->
        <section
            class="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 lg:grid-cols-4 dark:border-gray-700 dark:bg-gray-700"
            aria-label="Resumo da exposição"
        >
            <article
                v-for="tile in summaryTiles"
                :key="tile.key"
                class="bg-white px-4 py-3 dark:bg-gray-800"
            >
                <p
                    class="truncate text-[10px] font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                >
                    {{ tile.label }}
                </p>
                <p
                    v-if="loading && !searched"
                    class="mt-1.5 h-6 w-24 animate-pulse rounded-sm bg-gray-100 dark:bg-gray-700"
                ></p>
                <p
                    v-else
                    class="mt-1.5 truncate font-mono text-lg font-bold tabular-nums tracking-tight"
                    :class="tile.tone"
                    :title="tile.value"
                >
                    {{ tile.value }}
                </p>
                <p
                    class="mt-0.5 text-[10px] leading-relaxed text-gray-500 dark:text-gray-400"
                >
                    {{ tile.hint }}
                </p>
            </article>
        </section>

        <!-- Filtros -->
        <section
            class="mt-4 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
        >
            <div class="flex flex-wrap items-center gap-3">
                <div class="relative min-w-[16rem] grow">
                    <MagnifyingGlassIcon
                        class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        aria-hidden="true"
                    />
                    <input
                        v-model="filters.text"
                        type="search"
                        class="w-full rounded-md border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                        placeholder="Nome, e-mail, user ID, account ID ou UUID"
                        aria-label="Pesquisar produtor"
                        @input="debouncedFetch"
                    />
                </div>

                <!-- Escopo do saldo -->
                <div
                    class="flex items-center gap-px overflow-hidden rounded-md border border-gray-200 bg-gray-200 p-px dark:border-gray-700 dark:bg-gray-700"
                    role="group"
                    aria-label="Escopo do saldo"
                >
                    <button
                        v-for="option in scopeOptions"
                        :key="option.value"
                        type="button"
                        :aria-pressed="filters.scope === option.value"
                        class="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider transition-colors"
                        :class="
                            filters.scope === option.value
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white text-gray-500 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                        "
                        :title="option.hint"
                        @click="setScope(option.value)"
                    >
                        {{ option.label }}
                    </button>
                </div>

                <select
                    v-model="filters.origin"
                    class="rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                    aria-label="Origem"
                    @change="fetch(true)"
                >
                    <option value="">Todas as origens</option>
                    <option
                        v-for="origin in filterOptions.origins"
                        :key="origin"
                        :value="origin"
                    >
                        {{ origin }}
                    </option>
                </select>

                <select
                    v-model="filters.currency"
                    class="rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-xs uppercase text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                    aria-label="Moeda"
                    @change="fetch(true)"
                >
                    <option value="">Todas as moedas</option>
                    <option
                        v-for="currency in filterOptions.currencies"
                        :key="currency"
                        :value="currency"
                    >
                        {{ currency.toUpperCase() }}
                    </option>
                </select>

                <select
                    v-model="filters.audit"
                    class="rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                    aria-label="Auditoria"
                    @change="fetch(true)"
                >
                    <option value="">Auditoria: todas</option>
                    <option value="in_audit">Em auditoria</option>
                    <option value="not_in_audit">Fora de auditoria</option>
                </select>

                <div class="flex items-center gap-1.5">
                    <label
                        for="negative-balance-min"
                        class="text-[10px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500"
                    >
                        Déficit ≥
                    </label>
                    <input
                        id="negative-balance-min"
                        v-model="filters.minAmount"
                        type="number"
                        min="0"
                        step="100"
                        placeholder="0,00"
                        class="w-24 rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-right font-mono text-xs tabular-nums text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                        @input="debouncedFetch"
                    />
                </div>

                <button
                    v-if="hasActiveFilters"
                    type="button"
                    class="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
                    @click="clearFilters"
                >
                    <XMarkIcon class="h-3.5 w-3.5" aria-hidden="true" />
                    Limpar filtros
                </button>
            </div>
        </section>

        <!-- Tabela -->
        <section
            class="mt-4 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
        >
            <div class="overflow-auto" style="max-height: calc(100vh - 460px)">
                <table class="min-w-full border-collapse text-sm">
                    <thead
                        class="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900/95"
                    >
                        <tr>
                            <th
                                scope="col"
                                class="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                            >
                                <button
                                    type="button"
                                    class="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-white"
                                    @click="toggleSort('producer')"
                                >
                                    Produtor
                                    <component
                                        :is="sortIcon('producer')"
                                        class="h-3 w-3"
                                        :class="sortIconClass('producer')"
                                        aria-hidden="true"
                                    />
                                </button>
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                            >
                                Conta
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                            >
                                Auditoria
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                            >
                                Disponível
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                            >
                                Pendente
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                            >
                                Antecipação
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                            >
                                <button
                                    type="button"
                                    class="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-white"
                                    @click="toggleSort('deficit')"
                                >
                                    Déficit
                                    <component
                                        :is="sortIcon('deficit')"
                                        class="h-3 w-3"
                                        :class="sortIconClass('deficit')"
                                        aria-hidden="true"
                                    />
                                </button>
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                            >
                                Severidade
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                            >
                                <button
                                    type="button"
                                    class="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-white"
                                    @click="toggleSort('updated_at')"
                                >
                                    Atualizado
                                    <component
                                        :is="sortIcon('updated_at')"
                                        class="h-3 w-3"
                                        :class="sortIconClass('updated_at')"
                                        aria-hidden="true"
                                    />
                                </button>
                            </th>
                        </tr>
                    </thead>

                    <tbody
                        class="divide-y divide-gray-100 bg-white dark:divide-gray-700/60 dark:bg-gray-800"
                    >
                        <template v-if="loading">
                            <tr v-for="n in skeletonRows" :key="`sk-${n}`">
                                <td
                                    v-for="c in 9"
                                    :key="`sk-${n}-${c}`"
                                    class="px-4 py-3"
                                >
                                    <div
                                        class="h-3 animate-pulse rounded-sm bg-gray-100 dark:bg-gray-700"
                                    ></div>
                                </td>
                            </tr>
                        </template>

                        <tr v-else-if="rows.length === 0">
                            <td colspan="9" class="px-4 py-16 text-center">
                                <CheckBadgeIcon
                                    class="mx-auto h-8 w-8 text-emerald-500"
                                    aria-hidden="true"
                                />
                                <p
                                    class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-200"
                                >
                                    Nenhuma conta negativa neste escopo
                                </p>
                                <p
                                    class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                                >
                                    {{
                                        hasActiveFilters
                                            ? "Ajuste ou limpe os filtros para ampliar a busca."
                                            : "Nada a cobrar no momento."
                                    }}
                                </p>
                            </td>
                        </tr>

                        <template v-else>
                            <tr
                                v-for="row in rows"
                                :key="row.account_id"
                                class="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40"
                            >
                                <td class="px-4 py-2.5">
                                    <router-link
                                        :to="{
                                            name: 'user.statement',
                                            params: { user_id: row.user_id },
                                        }"
                                        class="block max-w-[18rem] truncate text-sm font-medium text-gray-900 hover:text-indigo-600 hover:underline dark:text-white dark:hover:text-indigo-400"
                                        :title="row.producer"
                                    >
                                        {{ row.producer || "—" }}
                                    </router-link>
                                    <span
                                        class="block max-w-[18rem] truncate text-[11px] text-gray-500 dark:text-gray-400"
                                        :title="row.email"
                                    >
                                        {{ row.email || "—" }}
                                    </span>
                                </td>

                                <td class="px-4 py-2.5">
                                    <span
                                        class="font-mono text-[11px] text-gray-600 dark:text-gray-300"
                                    >
                                        #{{ row.user_id }}
                                    </span>
                                    <span
                                        class="ml-1.5 rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                                    >
                                        {{ row.origin }} · {{ row.currency }}
                                    </span>
                                    <button
                                        type="button"
                                        class="ml-1 align-middle text-gray-400 transition-colors hover:text-indigo-500"
                                        :title="`Copiar UUID ${row.account_uuid}`"
                                        @click="copyUuid(row.account_uuid)"
                                    >
                                        <ClipboardDocumentIcon
                                            class="h-3.5 w-3.5"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </td>

                                <td class="px-4 py-2.5">
                                    <router-link
                                        v-if="row.audit"
                                        :to="{
                                            name: 'audit.interactions',
                                            params: { audit_id: row.audit.id },
                                        }"
                                        target="_blank"
                                        class="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 transition-colors hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:hover:bg-amber-500/20"
                                        :title="auditTooltip(row.audit)"
                                    >
                                        <span
                                            class="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                                        ></span>
                                        {{
                                            row.audit.stage_label ||
                                            "Em auditoria"
                                        }}
                                    </router-link>
                                    <span
                                        v-else
                                        class="text-[11px] text-gray-300 dark:text-gray-600"
                                        >—</span
                                    >
                                </td>

                                <td
                                    class="px-4 py-2.5 text-right font-mono text-xs tabular-nums"
                                    :class="amountClass(row.available_balance)"
                                >
                                    {{ formatCurrency(row.available_balance) }}
                                </td>
                                <td
                                    class="px-4 py-2.5 text-right font-mono text-xs tabular-nums"
                                    :class="amountClass(row.pending_balance)"
                                >
                                    {{ formatCurrency(row.pending_balance) }}
                                </td>
                                <td
                                    class="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-gray-500 dark:text-gray-400"
                                >
                                    {{
                                        formatCurrency(row.anticipation_balance)
                                    }}
                                </td>

                                <td
                                    class="px-4 py-2.5 text-right font-mono text-sm font-bold tabular-nums"
                                    :class="amountClass(scopedAmount(row))"
                                >
                                    {{ formatCurrency(scopedAmount(row)) }}
                                </td>

                                <td class="w-40 px-4 py-2.5">
                                    <div class="flex items-center gap-2">
                                        <div
                                            class="h-1.5 grow overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
                                        >
                                            <div
                                                class="h-full rounded-full transition-all"
                                                :class="severityBarClass(row)"
                                                :style="{
                                                    width: `${severityRatio(row)}%`,
                                                }"
                                            ></div>
                                        </div>
                                        <span
                                            class="w-10 shrink-0 text-right font-mono text-[10px] tabular-nums text-gray-400 dark:text-gray-500"
                                        >
                                            {{ severityRatio(row) }}%
                                        </span>
                                    </div>
                                </td>

                                <td
                                    class="px-4 py-2.5 text-right font-mono text-[11px] tabular-nums text-gray-500 dark:text-gray-400"
                                >
                                    {{ row.updated_at || "—" }}
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>

            <!-- Rodapé -->
            <div
                v-if="pagination.total > 0"
                class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-900/60"
            >
                <p class="text-[11px] text-gray-500 dark:text-gray-400">
                    Exibindo
                    <span class="font-mono tabular-nums">{{
                        pagination.from
                    }}</span>
                    a
                    <span class="font-mono tabular-nums">{{
                        pagination.to
                    }}</span>
                    de
                    <span class="font-mono tabular-nums">{{
                        pagination.total
                    }}</span>
                    contas
                </p>

                <div class="flex items-center gap-3">
                    <select
                        v-model.number="pagination.per_page"
                        class="rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-600 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                        aria-label="Registros por página"
                        @change="fetch(true)"
                    >
                        <option :value="25">25 / página</option>
                        <option :value="50">50 / página</option>
                        <option :value="100">100 / página</option>
                    </select>
                    <pagination
                        :pagination="pagination"
                        :offset="1"
                        @paginate="fetchPage"
                    ></pagination>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    BarsArrowDownIcon,
    BarsArrowUpIcon,
    CheckBadgeIcon,
    ChevronUpDownIcon,
    ClipboardDocumentIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
} from "@heroicons/vue/24/outline";
import Pagination from "../../../components/layouts/Pagination.vue";

const SCOPE_OPTIONS = [
    {
        value: "total",
        label: "Total",
        hint: "Saldo disponível somado ao pendente",
    },
    {
        value: "available",
        label: "Disponível",
        hint: "Apenas o saldo disponível da account",
    },
    {
        value: "pending",
        label: "Pendente",
        hint: "Apenas o saldo pendente da account",
    },
];

export default {
    components: {
        Pagination,
        ArrowDownTrayIcon,
        ArrowPathIcon,
        BarsArrowDownIcon,
        BarsArrowUpIcon,
        CheckBadgeIcon,
        ChevronUpDownIcon,
        ClipboardDocumentIcon,
        MagnifyingGlassIcon,
        XMarkIcon,
    },

    data() {
        return {
            loading: false,
            exporting: false,
            searched: false,
            lastUpdatedAt: null,
            debounceTimer: null,
            skeletonRows: 8,
            filters: {
                text: "",
                scope: "total",
                origin: "",
                currency: "",
                audit: "",
                minAmount: "",
            },
            filterOptions: {
                origins: [],
                currencies: [],
            },
            sort: {
                column: "deficit",
                direction: "desc",
            },
            summary: {
                accounts: 0,
                accounts_in_audit: 0,
                total_deficit: 0,
                worst_deficit: 0,
                average_deficit: 0,
            },
            rows: [],
            pagination: {
                total: 0,
                per_page: 25,
                from: 0,
                to: 0,
                current_page: 1,
                last_page: 1,
            },
        };
    },

    computed: {
        scopeOptions() {
            return SCOPE_OPTIONS;
        },

        scopeLabel() {
            return (
                SCOPE_OPTIONS.find(
                    (option) => option.value === this.filters.scope,
                )?.label ?? "Total"
            );
        },

        hasActiveFilters() {
            return (
                this.filters.text !== "" ||
                this.filters.origin !== "" ||
                this.filters.currency !== "" ||
                this.filters.audit !== "" ||
                this.filters.minAmount !== "" ||
                this.filters.scope !== "total"
            );
        },

        summaryTiles() {
            return [
                {
                    key: "accounts",
                    label: "Contas negativas",
                    value: new Intl.NumberFormat("pt-BR").format(
                        this.summary.accounts,
                    ),
                    hint: this.summary.accounts_in_audit
                        ? `${this.summary.accounts_in_audit} em auditoria · escopo ${this.scopeLabel.toLowerCase()}`
                        : `Nenhuma em auditoria · escopo ${this.scopeLabel.toLowerCase()}`,
                    tone: "text-gray-900 dark:text-white",
                },
                {
                    key: "total",
                    label: "Déficit total",
                    value: this.formatCurrency(this.summary.total_deficit),
                    hint: "Soma de todos os saldos negativos",
                    tone: "text-rose-600 dark:text-rose-400",
                },
                {
                    key: "worst",
                    label: "Pior déficit",
                    value: this.formatCurrency(this.summary.worst_deficit),
                    hint: "Conta individual mais negativa",
                    tone: "text-rose-600 dark:text-rose-400",
                },
                {
                    key: "average",
                    label: "Déficit médio",
                    value: this.formatCurrency(this.summary.average_deficit),
                    hint: "Média por conta negativa",
                    tone: "text-amber-600 dark:text-amber-400",
                },
            ];
        },
    },

    mounted() {
        this.fetch(true);
    },

    beforeUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    },

    methods: {
        requestParams() {
            const params = {
                scope: this.filters.scope,
                sort: this.sort.column,
                direction: this.sort.direction,
            };

            if (this.filters.text.trim() !== "") {
                params.text = this.filters.text.trim();
            }
            if (this.filters.origin !== "") {
                params.origin = this.filters.origin;
            }
            if (this.filters.currency !== "") {
                params.currency = this.filters.currency;
            }
            if (this.filters.audit !== "") {
                params.audit = this.filters.audit;
            }
            if (
                this.filters.minAmount !== "" &&
                Number(this.filters.minAmount) > 0
            ) {
                params.min_amount = Number(this.filters.minAmount);
            }

            return params;
        },

        fetch(reset = false) {
            if (reset) {
                this.pagination.current_page = 1;
            }

            this.loading = true;

            const params = {
                ...this.requestParams(),
                page: this.pagination.current_page,
                per_page: this.pagination.per_page,
            };

            axios
                .get("/api/financial/negative-balance", { params })
                .then(({ data }) => {
                    this.rows = Array.isArray(data.data) ? data.data : [];
                    this.summary = {
                        accounts: Number(data.summary?.accounts ?? 0),
                        accounts_in_audit: Number(
                            data.summary?.accounts_in_audit ?? 0,
                        ),
                        total_deficit: Number(data.summary?.total_deficit ?? 0),
                        worst_deficit: Number(data.summary?.worst_deficit ?? 0),
                        average_deficit: Number(
                            data.summary?.average_deficit ?? 0,
                        ),
                    };
                    this.filterOptions = {
                        origins: data.filters?.origins ?? [],
                        currencies: data.filters?.currencies ?? [],
                    };
                    this.pagination = {
                        ...this.pagination,
                        total: Number(data.total ?? 0),
                        from: Number(data.from ?? 0),
                        to: Number(data.to ?? 0),
                        current_page: Number(data.current_page ?? 1),
                        per_page: Number(
                            data.per_page ?? this.pagination.per_page,
                        ),
                        last_page: Number(data.last_page ?? 1),
                    };
                    this.searched = true;
                    this.lastUpdatedAt = new Date().toLocaleTimeString(
                        "pt-BR",
                        { hour: "2-digit", minute: "2-digit" },
                    );
                })
                .catch((error) => {
                    this.rows = [];
                    this.pagination = {
                        ...this.pagination,
                        total: 0,
                        from: 0,
                        to: 0,
                        current_page: 1,
                        last_page: 1,
                    };

                    if (this.$toast) {
                        this.$toast.error(
                            error?.response?.data?.message ||
                                "Erro ao carregar contas com saldo negativo.",
                        );
                    }

                    console.error("Erro ao buscar saldos negativos:", error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        debouncedFetch() {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            this.debounceTimer = setTimeout(() => this.fetch(true), 400);
        },

        fetchPage(page) {
            this.pagination.current_page = page;
            this.fetch();
        },

        setScope(scope) {
            if (this.filters.scope === scope) {
                return;
            }

            this.filters.scope = scope;
            this.fetch(true);
        },

        toggleSort(column) {
            if (this.sort.column === column) {
                this.sort.direction =
                    this.sort.direction === "desc" ? "asc" : "desc";
            } else {
                this.sort.column = column;
                this.sort.direction = "desc";
            }

            this.fetch(true);
        },

        sortIcon(column) {
            if (this.sort.column !== column) {
                return "ChevronUpDownIcon";
            }

            return this.sort.direction === "desc"
                ? "BarsArrowDownIcon"
                : "BarsArrowUpIcon";
        },

        sortIconClass(column) {
            return this.sort.column === column
                ? "text-indigo-500"
                : "text-gray-300 dark:text-gray-600";
        },

        clearFilters() {
            this.filters = {
                text: "",
                scope: "total",
                origin: "",
                currency: "",
                audit: "",
                minAmount: "",
            };
            this.fetch(true);
        },

        scopedAmount(row) {
            if (this.filters.scope === "available") {
                return row.available_balance;
            }
            if (this.filters.scope === "pending") {
                return row.pending_balance;
            }

            return row.total_balance;
        },

        severityRatio(row) {
            const worst = Math.abs(this.summary.worst_deficit);

            if (!worst) {
                return 0;
            }

            const ratio = (Math.abs(this.scopedAmount(row)) / worst) * 100;

            return Math.max(1, Math.min(100, Math.round(ratio)));
        },

        severityBarClass(row) {
            const ratio = this.severityRatio(row);

            if (ratio >= 66) {
                return "bg-rose-500";
            }
            if (ratio >= 33) {
                return "bg-amber-500";
            }

            return "bg-indigo-400";
        },

        auditTooltip(audit) {
            const stage = audit.stage_label ?? "sem estagio";
            const started = audit.started_at
                ? ` desde ${audit.started_at}`
                : "";

            return `Auditoria ${audit.status} (${stage})${started}. Abrir interacoes`;
        },

        amountClass(value) {
            if (value < 0) {
                return "text-rose-600 dark:text-rose-400";
            }
            if (value > 0) {
                return "text-gray-700 dark:text-gray-200";
            }

            return "text-gray-400 dark:text-gray-500";
        },

        formatCurrency(value) {
            return new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
            }).format(Number(value ?? 0));
        },

        copyUuid(uuid) {
            if (!navigator.clipboard) {
                return;
            }

            navigator.clipboard.writeText(uuid).then(() => {
                if (this.$toast) {
                    this.$toast.success("UUID da conta copiado.");
                }
            });
        },

        exportCsv() {
            this.exporting = true;

            axios
                .get("/api/financial/negative-balance/export", {
                    params: this.requestParams(),
                })
                .then(({ data }) => {
                    const csvContent = data.rows
                        .map((row) =>
                            row.map((cell) => `"${cell ?? ""}"`).join(";"),
                        )
                        .join("\n");
                    const blob = new Blob(["﻿" + csvContent], {
                        type: "text/csv;charset=utf-8;",
                    });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = data.filename;
                    link.click();
                    URL.revokeObjectURL(url);

                    if (data.truncated && this.$toast) {
                        this.$toast.warning(
                            "Exportação limitada às primeiras 20.000 contas.",
                        );
                    }
                })
                .catch((error) => {
                    if (this.$toast) {
                        this.$toast.error(
                            error?.response?.data?.message ||
                                "Erro ao exportar contas com saldo negativo.",
                        );
                    }

                    console.error("Erro ao exportar saldos negativos:", error);
                })
                .finally(() => {
                    this.exporting = false;
                });
        },
    },
};
</script>
